import { AppVisibilityMode } from '@electron/shared';
import {
  DEFAULT_SOUND_CHOICE,
  isNotificationSoundChoice,
  MAC_SYSTEM_SOUNDS,
  type NotificationSoundChoice,
} from '@electron/shared/sounds.types';
import { config, useUserConfig } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { electronApis } from '@/features/electron-api';
import useAutoStart from '@/hooks/useAutoStart';
import { settingsStore } from '@/stores/settings.store';
import { isMac } from '@/utils/capabilities';

import { SettingsSection } from '../settings-section';

export const SystemsSettings = () => {
  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>System</SettingsSection.Title>
      </SettingsSection.Header>
      <SettingsSection.Group>
        <AutoStartSettings />
        <SettingsSection.Seperator />
        <NotificationsEnabledSettings />
        <SettingsSection.Seperator />
        <NotificationSoundSettings />
        {isMac() && (
          <>
            <SettingsSection.Seperator />
            <NotificationSoundChoiceSettings />
          </>
        )}
        <SettingsSection.Seperator />
        <TimeFormatSettings />
        <SettingsSection.Seperator />
        <DockVisibilityToggle />
      </SettingsSection.Group>
    </SettingsSection.Root>
  );
};

const AutoStartSettings = () => {
  const { status, enable, disable } = useAutoStart();

  const handleAutoStartChange = async () => {
    if (status) await disable();
    else await enable();
  };

  if (status === null) return null;

  return (
    <SettingsSection.Item searchId="system.launch-on-startup">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Launch Littlebird on startup
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Automatically start Littlebird when you log in to your computer
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={status}
        onCheckedChange={handleAutoStartChange}
      />
    </SettingsSection.Item>
  );
};

const NotificationsEnabledSettings = observer(() => {
  return (
    <SettingsSection.Item searchId="system.notifications">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Notifications</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Show Littlebird notifications
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={settingsStore.lbNotificationsEnabled}
        onCheckedChange={(v) => settingsStore.setLbNotificationsEnabled(v)}
      />
    </SettingsSection.Item>
  );
});

const NotificationSoundSettings = observer(() => {
  const userConfig = useUserConfig();
  return (
    <SettingsSection.Item searchId="system.notification-sound">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Notification Sound
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Play a sound for notifications like meeting reminders and more
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={config.notifications.sound_enabled}
        onCheckedChange={(v) =>
          userConfig.notifications.set('sound_enabled', v)
        }
      />
    </SettingsSection.Item>
  );
});

const NotificationSoundChoiceSettings = observer(() => {
  const userConfig = useUserConfig();
  const storedChoice = config.notifications.sound;
  const soundChoice = isNotificationSoundChoice(storedChoice)
    ? storedChoice
    : DEFAULT_SOUND_CHOICE;
  const displayText =
    soundChoice === DEFAULT_SOUND_CHOICE ? 'Hummingbird' : soundChoice;

  const selectSound = (choice: NotificationSoundChoice) => {
    userConfig.notifications.set('sound', choice);
    void electronApis.lbNotifications.previewSoundChoice(choice);
  };

  return (
    <SettingsSection.Item searchId="system.notification-sound-choice">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Alert Sound</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Which sound plays for alerts like meeting reminders
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger aria-label={`Alert sound: ${displayText}`}>
          <span>{displayText}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          sideOffset={8}
          className="w-48 max-h-72"
        >
          <DropdownMenu.RadioGroup
            value={soundChoice}
            onValueChange={(value) => {
              if (isNotificationSoundChoice(value)) selectSound(value);
            }}
          >
            <DropdownMenu.RadioItem value={DEFAULT_SOUND_CHOICE}>
              Hummingbird
              <span className="ml-auto mr-5 text-xs text-foreground-muted">
                Default
              </span>
            </DropdownMenu.RadioItem>
            <DropdownMenu.Separator />
            {MAC_SYSTEM_SOUNDS.map((sound) => (
              <DropdownMenu.RadioItem key={sound} value={sound}>
                {sound}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const DockVisibilityToggle = observer(() => {
  const displayText =
    settingsStore.appVisibilityMode === AppVisibilityMode.StatusBar
      ? 'Status Bar'
      : 'Dock and Status Bar';

  return (
    <SettingsSection.Item searchId="system.show-app-in">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Show App In</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Control the visibility of the app when closed
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{displayText}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[12rem] gap-2"
        >
          <DropdownMenu.Item
            onSelect={() =>
              settingsStore.setAppVisibilityMode(
                AppVisibilityMode.DockAndStatusBar
              )
            }
          >
            Dock & Status Bar
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() =>
              settingsStore.setAppVisibilityMode(AppVisibilityMode.StatusBar)
            }
          >
            Status Bar Only
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

/** Detects browser/OS locale's default hour12 preference */
const getLocaleDefault12Hr = (): boolean => {
  const resolved = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
  }).resolvedOptions();
  return resolved.hour12 ?? true;
};

const TimeFormatSettings = observer(() => {
  // When null, show the effective locale default
  const effectiveFormat = settingsStore.timeFormat ?? getLocaleDefault12Hr();
  const displayText = effectiveFormat ? '12-hour' : '24-hour';

  return (
    <SettingsSection.Item searchId="system.time-format">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Time Format</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          How times are displayed throughout the app
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{displayText}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          sideOffset={8}
          className="w-32"
        >
          <DropdownMenu.Item onSelect={() => settingsStore.setTimeFormat(true)}>
            12-hour
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => settingsStore.setTimeFormat(false)}
          >
            24-hour
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});
